import React, { useState, useCallback } from 'react';
import { DashboardConfiguration, DashboardWidget } from '../../services/dashboardConfigurationService';
import WidgetPreview from './WidgetPreview';
import './DashboardPreview.css';

interface DashboardPreviewProps {
  dashboard: DashboardConfiguration;
  activeTabIndex: number;
  onWidgetEdit: (widget: DashboardWidget) => void;
  onWidgetDelete: (widgetIndex: number) => void;
  onWidgetMove: (widgetIndex: number, updates: Partial<DashboardWidget>) => void;
}

interface DragState {
  isDragging: boolean;
  draggedWidgetIndex: number | null;
  dragOffset: { x: number; y: number };
  startPosition: { x: number; y: number };
}

const DashboardPreview: React.FC<DashboardPreviewProps> = ({
  dashboard,
  activeTabIndex,
  onWidgetEdit,
  onWidgetDelete,
  onWidgetMove
}) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedWidgetIndex: null,
    dragOffset: { x: 0, y: 0 },
    startPosition: { x: 0, y: 0 }
  });

  const currentTab = dashboard.tabs?.[activeTabIndex];
  const widgets = currentTab?.widgets || [];

  // Grid configuration
  const GRID_COLUMNS = 12;
  const GRID_CELL_WIDTH = 80; // pixels
  const GRID_CELL_HEIGHT = 60; // pixels
  const GRID_GAP = 16; // pixels

  // Convert grid position to pixel position
  const gridToPixels = useCallback((gridX: number, gridY: number) => ({
    x: gridX * (GRID_CELL_WIDTH + GRID_GAP),
    y: gridY * (GRID_CELL_HEIGHT + GRID_GAP)
  }), []);

  // Convert pixel position to grid position
  const pixelsToGrid = useCallback((pixelX: number, pixelY: number) => ({
    x: Math.round(pixelX / (GRID_CELL_WIDTH + GRID_GAP)),
    y: Math.round(pixelY / (GRID_CELL_HEIGHT + GRID_GAP))
  }), []);

  // Handle widget drag start
  const handleDragStart = useCallback((widgetIndex: number, event: React.MouseEvent) => {
    event.preventDefault();
    
    const widget = widgets[widgetIndex];
    if (!widget) return;

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const containerRect = (event.currentTarget as HTMLElement).closest('.dashboard-grid')?.getBoundingClientRect();
    
    if (!containerRect) return;

    const startPosition = gridToPixels(widget.positionX, widget.positionY);
    
    setDragState({
      isDragging: true,
      draggedWidgetIndex: widgetIndex,
      dragOffset: {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      },
      startPosition
    });

    // Add global mouse event listeners
    const handleMouseMove = (e: MouseEvent) => {
      if (dragState.isDragging) return;
      
      const containerRect = document.querySelector('.dashboard-grid')?.getBoundingClientRect();
      if (!containerRect) return;

      const newPixelX = e.clientX - containerRect.left - dragState.dragOffset.x;
      const newPixelY = e.clientY - containerRect.top - dragState.dragOffset.y;
      
      const newGridPos = pixelsToGrid(Math.max(0, newPixelX), Math.max(0, newPixelY));
      
      // Ensure widget doesn't go out of bounds
      const maxX = GRID_COLUMNS - widget.width;
      const clampedX = Math.max(0, Math.min(maxX, newGridPos.x));
      const clampedY = Math.max(0, newGridPos.y);
      
      setDragState(prev => ({
        ...prev,
        startPosition: gridToPixels(clampedX, clampedY)
      }));
    };

    const handleMouseUp = () => {
      if (dragState.draggedWidgetIndex !== null) {
        const containerRect = document.querySelector('.dashboard-grid')?.getBoundingClientRect();
        if (containerRect) {
          const finalGridPos = pixelsToGrid(
            dragState.startPosition.x,
            dragState.startPosition.y
          );
          
          const maxX = GRID_COLUMNS - widget.width;
          const finalX = Math.max(0, Math.min(maxX, finalGridPos.x));
          const finalY = Math.max(0, finalGridPos.y);
          
          onWidgetMove(dragState.draggedWidgetIndex, {
            positionX: finalX,
            positionY: finalY
          });
        }
      }
      
      setDragState({
        isDragging: false,
        draggedWidgetIndex: null,
        dragOffset: { x: 0, y: 0 },
        startPosition: { x: 0, y: 0 }
      });
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [widgets, dragState, gridToPixels, pixelsToGrid, onWidgetMove]);

  // Render grid background
  const renderGridBackground = () => {
    const rows = Math.max(8, Math.max(...widgets.map(w => w.positionY + w.height)) + 2);
    const gridCells = [];

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < GRID_COLUMNS; col++) {
        gridCells.push(
          <div
            key={`${row}-${col}`}
            className="grid-cell"
            style={{
              left: col * (GRID_CELL_WIDTH + GRID_GAP),
              top: row * (GRID_CELL_HEIGHT + GRID_GAP),
              width: GRID_CELL_WIDTH,
              height: GRID_CELL_HEIGHT
            }}
          />
        );
      }
    }

    return gridCells;
  };

  // Check for widget overlaps
  const getOverlappingWidgets = (widget: DashboardWidget, excludeIndex?: number) => {
    return widgets.filter((otherWidget, index) => {
      if (index === excludeIndex) return false;
      
      const widget1 = {
        left: widget.positionX,
        right: widget.positionX + widget.width,
        top: widget.positionY,
        bottom: widget.positionY + widget.height
      };
      
      const widget2 = {
        left: otherWidget.positionX,
        right: otherWidget.positionX + otherWidget.width,
        top: otherWidget.positionY,
        bottom: otherWidget.positionY + otherWidget.height
      };
      
      return !(widget1.right <= widget2.left || 
               widget1.left >= widget2.right || 
               widget1.bottom <= widget2.top || 
               widget1.top >= widget2.bottom);
    });
  };

  if (!currentTab) {
    return (
      <div className="dashboard-preview">
        <div className="no-tab-selected">
          <h3>No Tab Selected</h3>
          <p>Please select a tab from the sidebar to start adding widgets.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-preview">
      {/* Tab Header */}
      <div className="tab-header">
        <div className="tab-info">
          <h2>{currentTab.tabName}</h2>
          {currentTab.tabDescription && (
            <p className="tab-description">{currentTab.tabDescription}</p>
          )}
        </div>
        <div className="tab-stats">
          <span className="widget-count">
            {widgets.length} widget{widgets.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div 
        className="dashboard-grid"
        style={{
          width: GRID_COLUMNS * (GRID_CELL_WIDTH + GRID_GAP),
          minHeight: Math.max(400, (Math.max(...widgets.map(w => w.positionY + w.height), 6) + 2) * (GRID_CELL_HEIGHT + GRID_GAP))
        }}
      >
        {/* Grid Background */}
        {renderGridBackground()}

        {/* Widgets */}
        {widgets.map((widget, index) => {
          const position = gridToPixels(widget.positionX, widget.positionY);
          const isDragging = dragState.isDragging && dragState.draggedWidgetIndex === index;
          const overlaps = getOverlappingWidgets(widget, index);
          const hasOverlap = overlaps.length > 0;
          
          return (
            <div
              key={widget.id || index}
              className={`widget-container ${isDragging ? 'dragging' : ''} ${hasOverlap ? 'overlap' : ''}`}
              style={{
                left: isDragging ? dragState.startPosition.x : position.x,
                top: isDragging ? dragState.startPosition.y : position.y,
                width: widget.width * (GRID_CELL_WIDTH + GRID_GAP) - GRID_GAP,
                height: widget.height * (GRID_CELL_HEIGHT + GRID_GAP) - GRID_GAP,
                zIndex: isDragging ? 1000 : 1
              }}
            >
              {/* Widget Actions */}
              <div className="widget-actions">
                <button
                  className="widget-action-btn edit-btn"
                  onClick={() => onWidgetEdit(widget)}
                  title="Edit Widget"
                >
                  ✏️
                </button>
                <button
                  className="widget-action-btn delete-btn"
                  onClick={() => onWidgetDelete(index)}
                  title="Delete Widget"
                >
                  🗑️
                </button>
                <button
                  className="widget-action-btn drag-btn"
                  onMouseDown={(e) => handleDragStart(index, e)}
                  title="Drag Widget"
                >
                  ⋮⋮
                </button>
              </div>

              {/* Widget Content */}
              <WidgetPreview 
                widget={widget} 
                onRemove={(widgetId) => onWidgetDelete(index)}
                onEdit={onWidgetEdit}
              />

              {/* Overlap Warning */}
              {hasOverlap && (
                <div className="overlap-warning">
                  ⚠️ Overlapping with {overlaps.length} widget{overlaps.length !== 1 ? 's' : ''}
                </div>
              )}

              {/* Widget Info */}
              <div className="widget-info-overlay">
                <span className="widget-size">{widget.width}×{widget.height}</span>
                <span className="widget-position">({widget.positionX}, {widget.positionY})</span>
              </div>
            </div>
          );
        })}

        {/* Empty State */}
        {widgets.length === 0 && (
          <div className="empty-dashboard">
            <div className="empty-content">
              <div className="empty-icon">🧩</div>
              <h3>No Widgets Added</h3>
              <p>Start building your dashboard by adding widgets from the sidebar.</p>
              <div className="empty-tips">
                <div className="tip">
                  <strong>💡 Tip:</strong> Use the "Add Widget" button in the sidebar to get started.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Grid Info */}
      <div className="grid-info">
        <span>Grid: {GRID_COLUMNS} columns × flexible rows</span>
        <span>Cell: {GRID_CELL_WIDTH}×{GRID_CELL_HEIGHT}px</span>
        <span>Gap: {GRID_GAP}px</span>
      </div>
    </div>
  );
};

export default DashboardPreview;
